import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import Numeral from 'numeral';

import { Alert, Button, Loader, Modal, Select, Table } from '@itds/elements';

import EditBandDrawer from '@carrier/pages/carriers/life_quotes/bands/Edit';

import Api from '@carrier/utils/Api';


const List = () => {
  const params = useParams();

  const [bands, setBands] = useState([]);
  const [filteredBands, setFilteredBands] = useState([]);
  const [band, setBand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error_message, setErrorMessage] = useState(null);
  const [success_message, setSuccessMessage] = useState(null);
  const [modal, setModal] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [productTypeFilter, setProductTypeFilter] = useState('');

  useEffect(() => {
    loadBands();
  }, []);

  useEffect(() => {
    if (productTypeFilter === '') {
      setFilteredBands(bands);
    } else {
      const filtered = bands.filter(band =>
        (band.product_type || 'N/A') === productTypeFilter
      );

      setFilteredBands(filtered);
    }
  }, [productTypeFilter, bands]);

  const loadBands = action => {
    setLoading(true);

    setTimeout(() => {
      setSuccessMessage(null);
      setErrorMessage(null);
    }, 2000);

    Api.loadBands({ carrier_id: params.id, data_version_id: params.version_id })
      .then(({ data }) => {
        setBands(data);
        setFilteredBands(data);
        setLoading(false);
        if (action === 'add') {
          setSuccessMessage('Bands Successfully Added');
        } else if (action === 'delete') {
          setSuccessMessage('Bands Successfully Deleted');
        }
      })
      .catch(() => {
        if (action === 'add') {
          setErrorMessage('Unable to add Bands at this time');
        } else if (action === 'delete') {
          setErrorMessage('Unable to delete Bands at this time');
        }
      });
  };

  const handleSave = () => {
    setBand(null);
    loadBands('add');
  };

  const handleDelete = (obj, e) => {
    e.stopPropagation();

    window.scrollTo(0, 0);
    setModal(true);
    setDeleteInfo(obj);
  };

  const confirmDelete = () => {
    window.scrollTo(0, 0);

    Api.deleteBand(deleteInfo.id)
      .then(() => {
        loadBands('delete');
        setModal(null);
        setDeleteInfo(null);
      })
      .catch(() => {
        setModal(null);
        setDeleteInfo(null);
      });
  };

  const handleProductTypeFilterChange = (e) => {
    setProductTypeFilter(e.target.value);
  };

  const getAvailableProductTypes = () => {
    const uniqueTypes = [...new Set(bands.map(band => band.product_type || 'N/A'))];

    return uniqueTypes.sort();
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      {success_message ? <Alert type='success'>{success_message}</Alert> : null}
      {error_message ? <Alert type='error'>{error_message}</Alert> : null}
      <EditBandDrawer
        band={band}
        onClose={setBand.bind(null, null)}
        onSave={handleSave}
        show={!!band}
      />

      {/* Filter and Add Button Row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{ fontWeight: '600', marginRight: '5px' }}>Filter by Product Type:</label>
          <Select
            onChange={handleProductTypeFilterChange}
            options={[
              { value: '', label: 'All Product Types' },
              ...getAvailableProductTypes().map(type => ({
                value: type,
                label: type
              }))
            ]}
            style={{ minWidth: '200px' }}
            value={productTypeFilter}
          />
        </div>

        <Button
          onClick={setBand.bind(null, {
            carrier_id: params.id,
            data_version_id: params.version_id
          })}
        >
          Add Band
        </Button>
      </div>

      <Table
        columns={[
          {
            id: 'band_name',
            label: 'Name'
          },
          {
            id: 'id',
            label: 'ID'
          },
          {
            id: 'product_type',
            label: 'Product Type',
            render: band => band.product_type || 'N/A'
          },
          {
            id: 'face_amount_start',
            label: 'Face Amount',
            render: band =>
              `${Numeral(band.face_amount_start).format('$0,0')} to 
              ${Numeral(band.face_amount_end).format('$0,0')}`
          },
          {
            id: 'age',
            label: 'Age',
            render: band => `${band.age_start} to ${band.age_end}`
          },
          {
            id: 'updated_at',
            label: 'Last Updated',
            render: band => format(new Date(band.updated_at), 'MM/dd/yyyy')
          },
          {
            id: 'actions',
            render: row => {
              return (
                <div>
                  <Button
                    color='RED'
                    inline={true}
                    onClick={handleDelete.bind(null, row)}
                    small={true}
                  >
                    Delete
                  </Button>
                </div>
              );
            },
            styles: { textAlign: 'right' }
          }
        ]}
        data={filteredBands}
        onRowClick={setBand}
      />
      {modal ? (
        <Modal
          buttons={[
            {
              color: 'RED',
              children: 'Confirm Delete',
              onClick: confirmDelete,
              style: { marginLeft: 'auto' }
            }
          ]}
          contained={true}
          onClose={() => {
            setModal(null);
          }}
          title={`Delete "${deleteInfo.band_name}"`}
        >
          <div style={{ padding: 40, fontSize: 16, textAlign: 'center', lineHeight: '1.5em' }}>
            Are you sure you want to delete "{deleteInfo.band_name}"?
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default List;